import Component from '@glimmer/component'
import type { TOC } from '@ember/component/template-only'
import { queryParam } from 'teacher-ui-client/decorators/query-param'
import {
  serialize as serializeSort,
  deserialize as deserializeSort,
  updateSortColumns,
} from '@blakeelearning/data-tables/utils/sorting'
import { action } from '@ember/object'
import type { SortingConfig } from '@blakeelearning/data-tables/utils/sorting'
import { getSortViewModel } from 'teacher-ui-client/utils/sorting'
import { t, formatDate } from 'ember-intl'
import { on } from '@ember/modifier'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import type { SpellingStudentWordsData } from 'teacher-ui-client/components/writing-legends/spelling-student-words'
import Tooltip from 'teacher-ui-client/components/tooltip'
import { eq } from 'ember-truth-helpers'

interface Signature {
  Element: HTMLTableElement
  Args: {
    isReloading?: boolean
    spellingStudentWords?: SpellingStudentWordsData
  }
}

export const SpellingStudentWordsTable: TOC<Signature> = <template>
  <table class="divide-dusty-black-200 relative table-fixed divide-y" ...attributes>
    <thead>
      <tr class="bg-white">
        <ThSort
          class="min-w-28"
          @sortKey="incorrect_word"
          @text={{t "components.reporting.tables.writingLegends.spelling.incorrectWord"}}
        />
        <ThSort
          class="min-w-32"
          @sortKey="likely_correct_words"
          @text={{t "components.reporting.tables.writingLegends.spelling.correctWord"}}
        />
        <ThSort
          class="min-w-32"
          @sortKey="spelling_issue"
          @text={{t "components.reporting.tables.writingLegends.spelling.spellingRule"}}
        />
        <ThSort
          class="min-w-32"
          @sortKey="last_attempted_at"
          @text={{t "components.reporting.tables.writingLegends.spelling.last5Attempts"}}
        />
      </tr>
    </thead>
    <tbody class="divide-dusty-black-200 divide-y {{if @isReloading 'opacity-50'}}">
      {{#each @spellingStudentWords.words as |word index|}}
        <tr class="bg-white" data-test-table-row="{{index}}">
          <td class="truncate p-3" data-test-cell="incorrect_word">
            {{word.incorrect_word}}
          </td>
          <td class="truncate p-3" data-test-cell="likely_correct_words">
            {{word.likely_correct_words}}
          </td>
          <td class="truncate p-3" data-test-cell="spelling_issue">
            {{#if (eq word.spelling_issue "Misspelling")}}
              -
            {{else}}
              {{word.spelling_issue}}
            {{/if}}
          </td>
          <td class="p-3" data-test-cell="attempts">
            <div class="flex">
              {{#each word.attempts as |attempt|}}
                <div>
                  {{#if attempt.is_correct}}
                    <PhosphorIcon
                      @icon="phosphor-check-bold"
                      class="bg-forest-green-300 mr-2 rounded-full p-2 text-white"
                    />
                  {{else}}
                    <PhosphorIcon
                      @icon="phosphor-x-bold"
                      class="bg-watermelony-red-350 mr-2 w-4 rounded-full p-2 text-white"
                    />
                  {{/if}}
                  <Tooltip
                    @text={{formatDate attempt.created_date locale="en-AU" day="2-digit" month="short" year="numeric"}}
                  />
                </div>
              {{/each}}
            </div>
          </td>
        </tr>
      {{/each}}
    </tbody>
  </table>
</template>

interface ThSortSignature {
  Element: HTMLTableCellElement
  Args: {
    text?: string
    sortKey: string
  }
}

class ThSort extends Component<ThSortSignature> {
  @queryParam('reporting/ui-scope/writing-legends/spelling') sort!: string

  get sortingConfig() {
    return deserializeSort(this.sort)
  }

  set sortingConfig(newSortConfig: SortingConfig) {
    this.sort = serializeSort(newSortConfig)
  }

  @action
  updateSort() {
    this.sortingConfig = updateSortColumns(this.sortingConfig, this.args.sortKey)
  }

  get sortViewModel() {
    return getSortViewModel(this.sortingConfig, this.args.sortKey)
  }

  <template>
    <th
      class="h-[80px] select-none"
      data-test-sort-key={{@sortKey}}
      aria-sort={{this.sortViewModel.ariaDirection}}
      ...attributes
    >
      <button
        type="button"
        class="flex h-full w-full items-center gap-x-3 p-3 text-left {{if @text 'justify-between' 'justify-end'}}"
        {{on "click" this.updateSort}}
      >
        {{#if @text}}<span class="text-dusty-black-250 font-normal uppercase">{{@text}}</span>{{/if}}
        <PhosphorIcon @icon={{this.sortViewModel.icon}} class="text-dusty-black-400 print:hidden" />
      </button>
    </th>
  </template>
}
